import { formatMoney } from '../../lib/formatters.ts'
import type { ProjectDrawer } from '../../api/cashReport'
import { sourceFocus, sourceHref } from './sourceLink.ts'

type Props = {
  open: boolean
  projectId: number | null
  versionId: string
  breakdown: ProjectDrawer | null
  loading: boolean
  error: string | null
  onClose: () => void
}

function moneyOf(cents?: number | null, fig?: { formatted?: string } | null) {
  if (fig?.formatted) return fig.formatted
  if (cents == null) return '—'
  return formatMoney(cents / 100)
}

export default function ProjectCashDrawer({
  open, projectId, versionId, breakdown, loading, error, onClose,
}: Props) {
  if (!open || projectId == null) return null
  const firstPa = [...(breakdown?.open_pay_apps || [])]
    .sort((a, b) => Number(a.pay_app_id) - Number(b.pay_app_id))[0]
  const fullHref = firstPa
    ? `/projects/${projectId}?sourceContext=cash-report&focus=pay_app:${firstPa.pay_app_id}`
    : `/projects/${projectId}?sourceContext=cash-report`

  return (
    <>
      <div className="cr-scrim" data-testid="cr-project-drawer-scrim" onClick={onClose} />
      <aside className="cr-drawer" data-testid="cr-project-drawer" aria-label="Project cash detail">
        <header className="cr-drawer-head">
          <div>
            <div className="cr-eyebrow">Project cash</div>
            <h2>{breakdown?.project_name || `Project ${projectId}`}</h2>
            <p className="cr-vid">#{projectId} · version {versionId}</p>
          </div>
          <button type="button" className="cr-btn" aria-label="Close project drawer" onClick={onClose}>×</button>
        </header>
        <div className="cr-drawer-body">
          {loading ? <div className="cr-state">Loading project…</div> : null}
          {error ? <div className="cr-state">{error}</div> : null}
          {breakdown?.contract ? (
            <div className="cr-drawer-summary">
              <div className="cr-drawer-metric">
                <span>Original contract</span>
                <strong>{moneyOf(null, breakdown.contract.original)}</strong>
              </div>
              <div className="cr-drawer-metric">
                <span>Revised contract</span>
                <strong>{moneyOf(null, breakdown.contract.revised)}</strong>
              </div>
              <div className="cr-drawer-metric">
                <span>Next risk</span>
                <strong>{breakdown.next_risk || 'Normal'}</strong>
              </div>
            </div>
          ) : null}

          <section className="cr-drawer-section">
            <h3>Open pay applications</h3>
            {(breakdown?.open_pay_apps || []).map((pa) => (
              <div key={pa.pay_app_id} className="cr-drawer-line" data-testid={`cr-drawer-pa-${pa.pay_app_id}`}>
                <span>
                  Pay app {pa.pay_app_id}
                  {pa.predicted_paid_at ? ` · ${pa.predicted_paid_at}` : ''}
                  {pa.status ? ` · ${pa.status}` : ''}
                  {pa.display ? ` · ${pa.display}` : ''}
                </span>
                <b>{moneyOf(pa.amount_cents)}</b>
              </div>
            ))}
          </section>

          <section className="cr-drawer-section">
            <h3>Subcontract Amounts</h3>
            <p className="cr-note">Not a cash line — commitment only.</p>
            {(breakdown?.subcontract_amounts || []).map((c, i) => (
              <div key={`${c.vendor_name || i}`} className="cr-drawer-line">
                <span>{c.vendor_name || 'Vendor'}</span>
                <b>{moneyOf(c.amount_cents)}</b>
              </div>
            ))}
          </section>

          <section className="cr-drawer-section">
            <h3>Approved vendor bills</h3>
            {(breakdown?.vendor_bills || []).map((line) => (
              <div key={sourceFocus(line)} className="cr-drawer-line" data-testid={`cr-drawer-bill-${line.source_id}`}>
                <span>
                  {line.label || 'Vendor bill'}
                  {sourceHref(line) ? <>{' '}<a href={sourceHref(line) || '#'} data-testid="cr-open-source">Open source</a></> : null}
                </span>
                <b>{formatMoney(Number(line.amount_cents) / 100)}</b>
              </div>
            ))}
          </section>

          <section className="cr-drawer-section">
            <h3>Retainage</h3>
            {(breakdown?.retainage || []).map((line) => (
              <div key={sourceFocus(line)} className="cr-drawer-line">
                <span>Held / release {line.effective_date || ''}</span>
                <b>{formatMoney(Number(line.amount_cents) / 100)}</b>
              </div>
            ))}
          </section>

          <section className="cr-drawer-section">
            <h3>Manual assumptions</h3>
            <p className="cr-note">Read-only here. Edit and delete ship in a later step.</p>
            {(breakdown?.assumptions || []).map((a) => (
              <div key={sourceFocus(a)} className="cr-drawer-line">
                <span>{a.label || 'Assumption'}</span>
                <b>{formatMoney(Number(a.amount_cents) / 100)}</b>
              </div>
            ))}
          </section>

          {breakdown?.per_project_margin?.projected_margin ? (
            <section className="cr-drawer-section">
              <h3>Project margin</h3>
              <div className="cr-drawer-line">
                <span>per_project_margin</span>
                <b>{breakdown.per_project_margin.projected_margin.formatted
                  || formatMoney((breakdown.per_project_margin.projected_margin.amount ?? 0))}</b>
              </div>
            </section>
          ) : null}
        </div>
        <footer className="cr-drawer-foot">
          <a className="cr-btn dark" data-testid="cr-open-full-project" href={fullHref}>Open full project</a>
          <button type="button" className="cr-btn" onClick={onClose}>Done</button>
        </footer>
      </aside>
    </>
  )
}
